/*
  Справочник судов - §3.4.2, вкладка настроек.

  Зачем он есть. Заявление о вынесении приказа подаётся мировому судье, и без
  адресата не подаётся вовсе. Определять подсудность по всей стране система не
  берётся - это задача за пределами первой версии; оператор заводит участок,
  отмечает относящиеся к нему дома, и дальше участок подтягивается к заявлению
  по адресу помещения сам.

  🔴 Два вида суда разведены по вкладкам (указание владельца 14.09.2026:
  «визуальное и физическое разграничение»). До этого вкладка показывала подряд
  четыре вещи - предупреждение, таблицу участков, дома выбранного участка и
  таблицу районных судов, - и нижняя таблица начиналась там, куда доезжали не
  все. Разграничение именно физическое: содержимое невыбранной вкладки не
  рисуется вовсе.

  Предупреждение стоит НАД вкладками и видно на всех: оно относится ко всему
  справочнику. С 02.11.2026 оно говорит, по скольким домам заявления не соберутся
  (`CourtHousesNotice`), а не сколько домов без вашего участка.

  С 21.10.2026 справочник не грузится целиком: страницы судов, домов участка и домов
  без участка режет сервер (`useCourtDirectory`), а перечитывание после любого действия
  - одна цифра `version`, от которой зависят все чтения.
*/

import { useReducer, useState } from 'react'

import { api } from '../api/client.js'
import { Lock } from './AccessContext.jsx'
import CourtBuildings from './CourtBuildings.jsx'
import CourtForm from './CourtForm.jsx'
import CourtRemoveConfirm from './CourtRemoveConfirm.jsx'
import CourtTable from './CourtTable.jsx'
import CourtHousesNotice from './CourtHousesNotice.jsx'
import CourtHousesTab from './CourtHousesTab.jsx'
import HousesTab from './HousesTab.jsx'
import Tabs, { TabPanel } from './Tabs.jsx'
import { Notice } from './Common.jsx'
import Modal from './Modal.jsx'
import { DISTRICT, MAGISTRATE, useCourtDirectory } from './useCourtDirectory.js'
import useSingleFlight from './useSingleFlight.js'

const HOUSES = 'houses'
const JURISDICTION = 'jurisdiction'

// «Подсудность домов» - первой и открытой (решение владельца 02.11.2026, вариант А):
// все дома портфеля с участком и протоколом, а свои участки - следующей вкладкой.
const COURT_TABS = [
  { key: JURISDICTION, title: 'Подсудность домов' },
  { key: MAGISTRATE, title: 'Участки мировых судей' },
  { key: DISTRICT, title: 'Районные суды' },
  { key: HOUSES, title: 'Дома' },
]

export default function SettingsCourtSections() {
  const [version, refresh] = useReducer((value) => value + 1, 0)
  const { magistrates, districts, summary, blocked, choices, error: loadError } =
    useCourtDirectory(version)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  const [removing, setRemoving] = useState(null)
  const [removeError, setRemoveError] = useState(null)
  const removal = useSingleFlight()
  // Не «открыто ли окно», а ВИД заводимого суда: кнопка стоит внутри вкладки, и
  // форма обязана открыться тем видом, ради которого на эту вкладку зашли.
  const [adding, setAdding] = useState(null)
  const [formError, setFormError] = useState(null)
  // Выбранный участок хранится строкой целиком, а не номером: строка может оказаться на
  // другой странице таблицы, и по номеру её уже не найти.
  const [selectedCourt, setSelectedCourt] = useState(null)
  const [tab, setTab] = useState(JURISDICTION)

  async function run(action) {
    setError(null)
    try {
      await action()
      refresh()
      return true
    } catch (failure) {
      setError(failure)
      return false
    }
  }

  async function create(body) {
    setBusy(true)
    setFormError(null)
    try {
      await api.createCourtSection(body)
      refresh()
      setAdding(null)
      return true
    } catch (failure) {
      // Отказ показывается ВНУТРИ окна: под закрывшимся его не видно вовсе, и
      // человек уходит уверенный, что суд заведён (чек-лист формы, пункт 1).
      setFormError(failure)
      return false
    } finally {
      setBusy(false)
    }
  }

  // Привязка из списка домов без участка (01.10.2026) - с 19.10.2026 через
  // мастер, вместе с протоколом собрания. Отказ НЕ ловится здесь, а уходит в
  // окно мастера или «Внести протокол»: общий блок наверху страницы под
  // открытым окном никто бы не увидел.
  async function attachFromList(id, { address, management, ...extra }) {
    await api.attachBuilding(id, address, management ?? {}, extra)
    refresh()
  }

  // Ручная привязка (ревью 30.10.2026): отказ уходит в саму форму, к её полям, - плашка
  // наверху страницы стояла далеко от формы внизу, под таблицей домов.
  async function attachByHand(id, address, management, extra) {
    await api.attachBuilding(id, address, management, extra)
    refresh()
  }

  async function saveProtocol(id, buildingId, management) {
    await api.setBuildingManagement(id, buildingId, management)
    refresh()
  }

  // Скан протокола (21.10.2026): отказ, как и у `saveProtocol`, уходит в окно. Отвязка
  // (23.10.2026) - так же: у дома со сканом её подтверждают в окне.
  async function saveProtocolScan(id, buildingId, file, values, replace) {
    await api.saveProtocolScan(id, buildingId, file, values, replace)
    refresh()
  }

  async function detach(id, buildingId) {
    await api.detachBuilding(id, buildingId)
    refresh()
  }

  async function preview(address) {
    try {
      return await api.courtSectionPreview(address)
    } catch (failure) {
      setError(failure)
      return null
    }
  }

  // Удаление: отказ показывается ВНУТРИ окна подтверждения, а повторное нажатие, пока
  // запрос в пути, пропускается (аудит 26.10.2026, причины 4 и 6). Прежде отказ вставал
  // над вкладками, за затемнением, и человек считал, что ничего не произошло.
  async function remove() {
    await removal.run(async () => {
      setRemoveError(null)
      try {
        await api.deleteCourtSection(removing.id)
      } catch (failure) {
        setRemoveError(failure)
        return
      }
      refresh()
      if (selectedCourt?.id === removing.id) setSelectedCourt(null)
      setRemoving(null)
    })
  }

  const failure = error ?? loadError ?? magistrates.error ?? districts.error
  const sections = magistrates.rows ?? []

  // Единственный участок выбирается сам: выбирать не из чего, а дома нужны
  // всегда. При нескольких участках выбор остаётся за человеком.
  const only = magistrates.total === 1 && sections.length === 1 ? sections[0] : null
  const selected = selectedCourt ?? only

  return (
    <>
      {failure ? <Notice error={failure} /> : null}

      <CourtHousesNotice summary={blocked} />

      <Tabs tabs={COURT_TABS} active={tab} onChange={setTab} nested />

      <TabPanel tabKey={JURISDICTION} active={tab}>
        <CourtHousesTab sections={choices.sections} version={version} onChanged={refresh} />
      </TabPanel>

      <TabPanel tabKey={MAGISTRATE} active={tab}>
        <div className="row row--between">
          <p className="muted">
            Заявление о вынесении приказа подаётся мировому судье. Участок определяется по адресу
            помещения - через дома, отмеченные ниже.
          </p>
          <Lock>
            <button className="button" type="button" onClick={() => setAdding(MAGISTRATE)}>
              Добавить участок
            </button>
          </Lock>
        </div>
        <CourtTable
          list={magistrates}
          districts={choices.districts}
          selectedId={selected?.id}
          onSelect={setSelectedCourt}
          onSetDistrict={(court, districtId) =>
            run(() => api.updateCourtSection(court.id, { ...court, district_court_id: districtId || null }))
          }
          onDelete={setRemoving}
        />

        {/*
          Дома открываются выбором строки. Единственный участок выбирается сам:
          лишнее нажатие там, где выбирать не из чего, - работа без смысла.
        */}
        {selected ? (
          <CourtBuildings
            key={selected.id}
            section={selected}
            sections={choices.sections}
            unassignedCount={summary?.houses_count ?? 0}
            version={version}
            onPreview={preview}
            onAttach={attachByHand}
            onAttachFromList={attachFromList}
            onDetach={detach}
            onManagement={(id, buildingId, management) =>
              run(() => api.setBuildingManagement(id, buildingId, management))
            }
            onProtocol={saveProtocol}
            onReadScan={api.readProtocolScan}
            onSaveScan={saveProtocolScan}
          />
        ) : magistrates.total > 0 ? (
          <p className="muted">Выберите участок в таблице, чтобы увидеть его дома.</p>
        ) : null}
      </TabPanel>

      {/* Районные суды - адресаты исков. Домов у них нет: район определяется
          через участок, которому дом отнесён, поэтому панели домов здесь не
          появляется и появиться не может. */}
      <TabPanel tabKey={DISTRICT} active={tab}>
        <div className="row row--between">
          <p className="muted">
            Исковое заявление подаётся в районный суд. Дома к нему не привязываются: район известен
            через участок, которому дом отнесён.
          </p>
          <Lock>
            <button className="button" type="button" onClick={() => setAdding(DISTRICT)}>
              Добавить районный суд
            </button>
          </Lock>
        </div>
        <CourtTable list={districts} onDelete={setRemoving} />
      </TabPanel>

      {/*
        Дома портфеля - весь список, а не только заведённые (указание владельца
        14.09.2026). Вкладка грузит их сама, когда её открывают: счета считает
        движок триажа, и платить за это при каждом входе в справочник незачем.
      */}
      <TabPanel tabKey={HOUSES} active={tab}>
        <HousesTab />
      </TabPanel>

      {adding ? (
        <Modal title="Новый суд" onClose={() => setAdding(null)}>
          <CourtForm
            initialKind={adding}
            districts={choices.districts}
            busy={busy}
            error={formError}
            onCancel={() => setAdding(null)}
            onCreate={create}
          />
        </Modal>
      ) : null}

      {removing ? (
        <CourtRemoveConfirm
          court={removing}
          busy={removal.busy}
          error={removeError}
          onCancel={() => {
            setRemoving(null)
            setRemoveError(null)
          }}
          onConfirm={remove}
        />
      ) : null}
    </>
  )
}
